import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { formatDate } from '../../utils/dateUtils'
import config from '../../config/config'
import * as Routes from '../../config/routes'

import {
  getPoiTypeLabelFromPoi,
  getLevel,
  getElevationGain,
  getElevationLoss,
  getTechnicity,
  getSurface,
  getSurfaceType,
  getDistance,
  getAltitude,
  getPhone,
  getEmail,
  getEquivalentKm,
  getType,
  slugify,
} from '../../utils/utils'

import { useLanguage } from '../../contexts/LanguageContext'
import { usePois } from '../../contexts/PoisContext'
import { POI_PATH, EVENT_PATH } from '../../config/routes'

const PoiTechnicalSheet = ({ poi, isButtonShow = true }) => {
  const { t } = useTranslation()
  const { activeLanguage } = useLanguage()
  const { pois } = usePois()

  const getPoiCreatedAt = (poi) =>
    poi?.createdAt ? formatDate(poi.createdAt) : ''
  const getPoiUpdatedAt = (poi) =>
    poi?.updatedAt ? formatDate(poi.updatedAt) : ''

  const linkPath = poi.isEvent ? EVENT_PATH : POI_PATH

  // POI lié (ex: VILLAGE de rattachement)
  const relatedVillage = pois.find(
    (p) => p.poiType.name === 'VILLAGE' && p.city?.name === poi.city?.name,
  )

  const villageLink = relatedVillage ? (
    <Link
      to={POI_PATH.replace(':slug', slugify(relatedVillage.labelFr)).replace(
        ':id',
        relatedVillage.id,
      )}
    >
      {poi.city?.label}
    </Link>
  ) : (
    poi.city?.label
  )

  // Fonction pour récupérer dynamiquement le path pour un poiType
  const getPoiTypePath = (poiType) => {
    const key = `${poiType}_PATH`
    return Routes[key] || null
  }

  const getPoiTypeLink = () => {
    const type = poi?.poiType?.name
    const label = getPoiTypeLabelFromPoi(poi, activeLanguage)

    if (type && config.poiTypesWithThumbnail.includes(type)) {
      const path = getPoiTypePath(type)
      if (path) {
        return (
          <Link to={path} className="text-decoration-underline">
            {label}
          </Link>
        )
      }
    }

    return label
  }

  const entries = [
    {
      label: t('POI_TYPE_LABEL'),
      value: getPoiTypeLink(),
    },
    getLevel(poi) !== null && {
      label: t('LEVEL_LABEL'),
      value: t(getLevel(poi)),
    },
    poi.numero && { label: 'N°', value: poi.numero },
    { label: t('POI_VILLAGE_LABEL'), value: villageLink },
    getElevationLoss(poi) !== null && {
      label: t('ELEVATION_LOSS_LABEL'),
      value: `${getElevationLoss(poi)}m`,
    },
    getElevationGain(poi) !== null && {
      label: t('ELEVATION_GAIN_LABEL'),
      value: `${getElevationGain(poi)}m`,
    },
    poi.altiMin && { label: t('ALTITUDE_MIN_LABEL'), value: `${poi.altiMin}m` },
    poi.altiMax && { label: t('ALTITUDE_MAX_LABEL'), value: `${poi.altiMax}m` },
    getTechnicity(poi) && {
      label: t('TECHNICITY_LABEL'),
      value: t(getTechnicity(poi)),
    },
    getSurfaceType(poi) && {
      label: t('SURFACE_TYPE_LABEL'),
      value: t(getSurfaceType(poi)),
    },
    getType(poi) && {
      label: t('TYPE'),
      value: t(getType(poi)),
    },
    getDistance(poi) !== null && {
      label: t('DISTANCE_LABEL'),
      value: `${getDistance(poi, true)}km`,
    },
    getAltitude(poi) !== null &&
      getAltitude(poi) > 0 && {
        label: t('ALTITUDE_LABEL'),
        value: `${getAltitude(poi)}m`,
      },
    getPhone(poi) && { label: t('PHONE_LABEL'), value: getPhone(poi) },
    getEmail(poi) && { label: t('EMAIL_LABEL'), value: getEmail(poi) },
    getEquivalentKm(poi) !== null && {
      label: t('EQUIVALENT_KM_LABEL'),
      value: `${getEquivalentKm(poi)}km`,
    },
    getSurface(poi) && {
      label: t('SURFACE_LABEL'),
      value: `${t(getSurface(poi))}km²`,
    },
    { label: t('POI_CREATED_AT_LABEL'), value: getPoiCreatedAt(poi) },
    { label: t('POI_UPDATED_AT_LABEL'), value: getPoiUpdatedAt(poi) },
  ].filter(Boolean)

  return (
    <div className="summary-box">
      <dl className="row">
        {entries.map((entry, idx) => (
          <div key={idx} className="col-12 col-sm-6 mb-3">
            <dl className="row mb-0">
              <dt className="col-4 mb-0">{entry.label}:</dt>
              <dd className="col-8 mb-0">{entry.value}</dd>
            </dl>
          </div>
        ))}
      </dl>
      {isButtonShow && (
        <div className="mt-4">
          <Link
            to={linkPath
              .replace(':slug', slugify(poi.labelFr))
              .replace(':id', poi.id)}
            className="btn btn-primary"
          >
            {t('SHOW')}
          </Link>
        </div>
      )}
    </div>
  )
}

export default PoiTechnicalSheet
